import { Feature } from "ol";
import { transformExtent } from "ol/proj";
import * as mapSettings from "../assets/map.json";
import {
    addParkingMachinesBatch,
    checkMapWidthLimit,
    invisibleStyle,
    loadParkingZonesIntoMap,
    map,
    parkingMachines,
    parkingMachineStyle,
    parkingZones,
    styleElement,
    visibleZonesInViewport,
} from "../components/Map";
import { fetchData } from "./api";
import { TFilter } from "../model/type/MapSearch/TFilter";

const { minZoomZonesVisible } = mapSettings;

export interface FilterResult {
    visibleZones: any[];
}

export interface LoadResult {
    success: boolean;
    visibleZones: any[];
    raLevelsData: Record<string, string>;
}

class MapService {
    private static instance: MapService;
    private selectedFeature: Feature | null = null;
    private hoveredFeature: Feature | null = null;

    private constructor() {}

    static getInstance(): MapService {
        if (!MapService.instance) {
            MapService.instance = new MapService();
        }
        return MapService.instance;
    }

    selectFeature(feature: Feature | null): void {
        this.selectedFeature = feature;
    }

    getSelectedFeature(): Feature | null {
        return this.selectedFeature;
    }

    deselectFeature(): void {
        if (this.selectedFeature) {
            const featureType = this.selectedFeature.getProperties().featureType;
            if (featureType === "parkingZone") {
                this.selectedFeature.setStyle(styleElement(this.selectedFeature.getProperties().sectionCategoryColor, false));
            } else if (featureType === "parkingMachine") {
                this.selectedFeature.setStyle(parkingMachineStyle("#1D4F8B", this.selectedFeature));
            }
            this.selectedFeature = null;
        }
    }

    setHoveredFeature(feature: Feature | null): void {
        this.hoveredFeature = feature;
    }

    getHoveredFeature(): Feature | null {
        return this.hoveredFeature;
    }

    isZoomSufficient(): boolean {
        return map.getView().getZoom() >= minZoomZonesVisible;
    }

    getVisibleZonesInViewport(): any[] {
        return visibleZonesInViewport();
    }

    /**
     * Apply filter to existing features on the map
     * Updates visibility of zones and parking machines based on filter criteria
     */
    applyFilter(filter: TFilter): FilterResult {
        if (!this.isZoomSufficient()) {
            return { visibleZones: [] };
        }

        parkingZones
            .getSource()
            .getFeatures()
            .forEach((feature) => {
                const properties = feature.getProperties();

                // Skip selected feature - it should always remain visible with selected style
                if (this.selectedFeature === feature) {
                    feature.set("visible", true, true);
                    return;
                }

                const isZoneSelected = filter.zones[properties.sectionCategoryID] === true;
                const isWithinTimeLimit = !filter.parking.to || filter.parking.to / 60 <= properties.tariffInfo?.maxHour;
                const shouldBeVisible = isZoneSelected && isWithinTimeLimit;

                if (shouldBeVisible) {
                    feature.setStyle(styleElement(properties.sectionCategoryColor, false));
                    feature.set("visible", true, true);
                } else {
                    feature.setStyle(invisibleStyle);
                    feature.set("visible", false, true);
                }
            });

        parkingMachines
            .getSource()
            .getFeatures()
            .forEach((feature) => {
                const properties = feature.getProperties();

                // Skip selected feature - it should always remain visible with selected style
                if (feature === this.selectedFeature) {
                    feature.set("visible", true, true);
                    return;
                }

                // Get the zone category that this machine belongs to
                const parkingZone = parkingZones.getSource().getFeatureById(properties.sectionID);
                let sectionCategoryID: string | undefined;

                if (parkingZone instanceof Feature) {
                    sectionCategoryID = parkingZone.getProperties().sectionCategoryID;
                }

                const isZoneSelected = sectionCategoryID && filter.zones[sectionCategoryID] === true;
                const isWithinTimeLimit = !filter.parking.to || filter.parking.to / 60 <= properties.tariffInfo?.maxHour;
                const shouldBeVisible = isZoneSelected && isWithinTimeLimit;

                if (shouldBeVisible) {
                    feature.setStyle(parkingMachineStyle("#1D4F8B", feature));
                    feature.set("visible", true, true);
                } else {
                    feature.setStyle(invisibleStyle);
                    feature.set("visible", false, true);
                }
            });

        return { visibleZones: visibleZonesInViewport() };
    }

    /**
     * Load map data (zones and parking machines) based on current map position
     * Returns the loaded data and applies filter
     */
    async loadDataByPosition(filter: TFilter, isFirstLoad: boolean = false): Promise<LoadResult> {
        const raLevelsData: Record<string, string> = {};

        if (!this.isZoomSufficient()) {
            // Only hide layers if there's no selected feature
            if (!this.selectedFeature) {
                // Only change visibility if not already hidden
                if (parkingZones.getVisible()) {
                    parkingZones.setVisible(false);
                }
                if (parkingMachines.getVisible()) {
                    parkingMachines.setVisible(false);
                }
            }
            return {
                success: false,
                visibleZones: [],
                raLevelsData,
            };
        }

        if (!checkMapWidthLimit()) {
            console.warn("Map zoom level too far out - clearing map data");
            parkingZones.getSource()?.clear();
            parkingMachines.getSource()?.clear();
            return {
                success: false,
                visibleZones: [],
                raLevelsData,
            };
        }

        const boundaries: Array<number> = transformExtent(map.getView().calculateExtent(map.getSize()), "EPSG:5514", "EPSG:4326");

        if (!boundaries[0] || !boundaries[1] || !boundaries[2] || !boundaries[3]) {
            return {
                success: false,
                visibleZones: [],
                raLevelsData,
            };
        }

        // Fetch sections
        const sectionsData = await fetchData("StreetParking/sections/2/byBoundaries", "GET", "url", {
            minLatitude: boundaries[1],
            minLongitude: boundaries[0],
            maxLatitude: boundaries[3],
            maxLongitude: boundaries[2],
        });

        if (sectionsData.length > 0) {
            sectionsData.forEach((section: any) => {
                raLevelsData[section.sectionID] = [section.raLevel2, section.raLevel3].filter(Boolean).join("; ");
            });
        }

        // Fetch parking machines
        const parkingMachinesList = await fetchData("StreetParking/parkMachines/2/byBoundaries", "GET", "url", {
            minLatitude: boundaries[1],
            minLongitude: boundaries[0],
            maxLatitude: boundaries[3],
            maxLongitude: boundaries[2],
        });

        if (parkingMachinesList.length > 0) {
            parkingMachinesList.forEach((parkingMachine: any) => {
                raLevelsData[parkingMachine.parkMachineID] = [parkingMachine.raLevel2, parkingMachine.raLevel3]
                    .filter(Boolean)
                    .join("; ");
            });
        }

        // Check zoom BEFORE adding features - it could have changed during fetch
        if (!this.isZoomSufficient()) {
            if (parkingZones.getVisible()) {
                parkingZones.setVisible(false);
            }
            if (parkingMachines.getVisible()) {
                parkingMachines.setVisible(false);
            }
            return {
                success: false,
                visibleZones: [],
                raLevelsData,
            };
        }

        // Add features to map (these functions already check for duplicates)
        addParkingMachinesBatch(parkingMachinesList);
        await loadParkingZonesIntoMap(sectionsData, isFirstLoad);

        // Apply filter and get visible zones
        const filterResult = this.applyFilter(filter);

        // Only change visibility if not already visible
        if (!parkingZones.getVisible()) {
            parkingZones.setVisible(true);
        }
        if (!parkingMachines.getVisible()) {
            parkingMachines.setVisible(true);
        }

        return {
            success: true,
            visibleZones: filterResult.visibleZones,
            raLevelsData,
            sectionsData,
        } as LoadResult & { sectionsData: any[] };
    }

    /**
     * Validate if detail zone is still within loaded bounds
     */
    validateDetailZoneInBounds(detailZone: any, loadedSections: any[]): boolean {
        if (!detailZone) return true;

        if (detailZone.properties.featureType === "parkingZone") {
            return loadedSections.some((section) => section.sectionCode === detailZone.properties.sectionCode);
        }

        return true;
    }
}

export default MapService;
